// ─── components/Shared.jsx — reusable bits ───────────────────────────────────
import { useState, useRef, useEffect } from "react";
import { LABEL_COLORS, LABEL_NAMES, checklistProgress, uid } from "../store.js";
import Swal from "sweetalert2";
import config from "../config";

// ── colour palette ────────────────────────────────────────────────────────────
export const C = {
  bg:         "#1d2125",
  surface:    "#22272b",
  surfaceHov: "#2d333b",
  border:     "#2d333b",
  border2:    "#373e47",
  borderHov:  "#444c56",
  accent:     "#579dff",
  accentHov:  "#4f8fef",
  text:       "#cdd9e5",
  textMid:    "#b6c2cf",
  textMuted:  "#768390",
  textDim:    "#545d68",
  green:      "#4bce97",
  red:        "#f87168",
  orange:     "#f5a623",
  purple:     "#9f8fef",
};

// ── LabelPill ─────────────────────────────────────────────────────────────────
export function LabelPill({ color, small }) {
  const s = LABEL_COLORS[color] || LABEL_COLORS.gray;
  if (small) return (
    <div style={{ background: s.bg, borderRadius: 3, height: 6, width: 32 }} title={LABEL_NAMES[color]} />
  );
  return (
    <div style={{ background: s.bg, color: s.text, borderRadius: 4, padding: "2px 7px", fontSize: 10, fontWeight: 700, letterSpacing: "0.03em" }}>
      {LABEL_NAMES[color]}
    </div>
  );
}

// ── ChecklistBar ──────────────────────────────────────────────────────────────
export function ChecklistBar({ pct, compact }) {
  const color = pct === 100 ? C.green : pct > 50 ? C.accent : C.orange;
  if (compact) return (
    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
      <span style={{ fontSize: 10, color: pct === 100 ? C.green : C.textMuted, fontWeight: 600 }}>✓ {pct}%</span>
      <div style={{ width: 60, height: 3, background: C.border, borderRadius: 2, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 2, transition: "width .3s" }} />
      </div>
    </div>
  );
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.textMuted, marginBottom: 4 }}>
        <span>Checklist</span>
        <span style={{ fontWeight: 700, color: pct === 100 ? C.green : C.textMid }}>{pct}%</span>
      </div>
      <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 3, transition: "width .3s" }} />
      </div>
    </div>
  );
}

// ── Btn ───────────────────────────────────────────────────────────────────────
export function Btn({ label, onClick, primary, danger, small, icon }) {
  const [hov, setHov] = useState(false);
  let bg = "transparent", border = `1px solid ${C.border2}`, color = hov ? C.textMid : C.textMuted;
  if (primary) { bg = hov ? C.accentHov : C.accent; border = "none"; color = "#fff"; }
  if (danger)  { bg = hov ? "#c0392b" : C.red;       border = "none"; color = "#fff"; }
  return (
    <button onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: hov && !primary && !danger ? C.surfaceHov : bg, border, borderRadius: 6, padding: small ? "5px 10px" : "7px 14px", fontSize: small ? 11 : 12, fontWeight: 600, color, cursor: "pointer", fontFamily: "inherit", transition: "all .12s", display: "flex", alignItems: "center", gap: 5 }}>
      {icon && <span>{icon}</span>}{label}
    </button>
  );
}

// ── SidebarBtn ────────────────────────────────────────────────────────────────
export function SidebarBtn({ icon, label, onClick, colorVal }) {
  const [hov, setHov] = useState(false);
  return (
    <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: "flex", alignItems: "center", gap: 7, padding: "7px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 500, color: hov ? (colorVal || C.text) : (colorVal || C.textMuted), background: hov ? C.surfaceHov : C.surface, marginBottom: 4, transition: "all .12s", border: `1px solid ${hov ? C.borderHov : C.border}` }}>
      <span>{icon}</span>{label}
    </div>
  );
}

// ── AddCardInline ─────────────────────────────────────────────────────────────
export function AddCardInline({ colId, onAdd, onCancel }) {
  const [title, setTitle] = useState("");
  const ref = useRef();
  useEffect(() => { ref.current?.focus(); }, []);

  const submit = () => {
    if (!title.trim()) return onCancel();
    onAdd(colId, title.trim());
    onCancel();
  };

  return (
    <div>
      <textarea ref={ref} value={title} onChange={e => setTitle(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } if (e.key === "Escape") onCancel(); }}
        placeholder="Enter a title for this card..."
        style={{ width: "100%", background: C.surface, border: `1px solid ${C.accent}`, borderRadius: 6, padding: "8px 10px", fontSize: 13, color: C.text, resize: "none", outline: "none", fontFamily: "inherit", minHeight: 60, lineHeight: 1.5 }} />
      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        <Btn label="Add card" onClick={submit} primary />
        <button onClick={onCancel} style={{ background: "transparent", border: "none", fontSize: 22, color: C.textMuted, cursor: "pointer" }}>×</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── CreateTicketSection ───────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
const PRIORITY_COLORS = {
  Critical: { border: "#f8716888", text: "#f87168", dim: "#f8716818" },
  High:     { border: "#fb923c88", text: "#fb923c", dim: "#fb923c18" },
  Medium:   { border: "#fbbf2488", text: "#fbbf24", dim: "#fbbf2418" },
  Low:      { border: "#34d39988", text: "#34d399", dim: "#34d39918" },
};

const STATUS_TO_PRIORITY = {
  todo: "Medium", inprogress: "High", pending: "High", complete: "Low",
};

const suggestDeadline = (p) => {
  const d = new Date();
  if (p === "Critical")     d.setHours(d.getHours() + 4);
  else if (p === "High")    d.setDate(d.getDate() + 1);
  else if (p === "Medium")  d.setDate(d.getDate() + 3);
  else                      d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 16);
};

const genTicketNumber = () => {
  const n = new Date();
  const y  = n.getFullYear();
  const mo = String(n.getMonth() + 1).padStart(2, "0");
  const d  = String(n.getDate()).padStart(2, "0");
  const h  = String(n.getHours()).padStart(2, "0");
  const mi = String(n.getMinutes()).padStart(2, "0");
  const sc = String(n.getSeconds()).padStart(2, "0");
  const ms = String(n.getMilliseconds()).padStart(3, "0");
  return `TICKET-${y}${mo}${d}-${h}${mi}${sc}-${ms}`;
};

const swalDark = {
  background: "#1e293b", color: "#f1f5f9",
  confirmButtonColor: "#579dff", cancelButtonColor: "#334155",
};

function CreateTicketSection({ card, onTicketCreated, assignedTo }) {
  const initTicket = card?.ticketRef || null;
  const [enabled,        setEnabled]        = useState(!!initTicket);
  const [expanded,       setExpanded]        = useState(false);
  const [creating,       setCreating]        = useState(false);
  const [existingTicket, setExistingTicket]  = useState(initTicket);
  const [priority,       setPriority]        = useState(STATUS_TO_PRIORITY[card?.status] || "Medium");
  const [deadline,       setDeadline]        = useState("");

  useEffect(() => {
    if (card?.ticketRef && card.ticketRef !== existingTicket) {
      setExistingTicket(card.ticketRef);
      setEnabled(true);
    }
  }, [card?.ticketRef]);

  const minDeadline = (() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 30);
    return d.toISOString().slice(0, 16);
  })();

  const handleToggle = () => {
    if (existingTicket) return;
    const next = !enabled;
    setEnabled(next);
    setExpanded(next);
    if (next && !deadline) setDeadline(suggestDeadline(priority));
  };

  const handlePriorityChange = (p) => {
    setPriority(p);
    setDeadline(suggestDeadline(p));
  };

  const handleCreate = async () => {
    if (!deadline) {
      await Swal.fire({ ...swalDark, icon: "warning", title: "Deadline Required", text: "Please set a deadline before creating the ticket." });
      return;
    }

    const ticketNumber = genTicketNumber();
    const now = new Date().toISOString();
    const timeGoalMap = { Critical: "4 hours", High: "24 hours", Medium: "48 hours", Low: "48 hours" };

    const checklistText = Array.isArray(card.checklist) && card.checklist.length > 0
      ? "\n\nChecklist:\n" + card.checklist.map((i, idx) =>
          `${idx+1}. [${i.done ? "x" : " "}] ${i.text || String(i)}`).join("\n")
      : "";

    const labelsText = Array.isArray(card.labels) && card.labels.length > 0
      ? card.labels.join(", ") : "";

    const payload = {
      // ── Core (matches CreateTicketFileModal exactly) ──────────────────────
      TicketNumber:        ticketNumber,
      Subject:             card.title || "Untitled Card",
      Summary:             card.title || "Untitled Card",
      Description:         (card.desc || card.description || "") + checklistText,
      Name:                card.createdBy || "System",
      Reporter:            card.createdBy || "System",
      Email:               "",
      Contact:             "N/A",
      UserId:              "system",
      Assignees:           assignedTo || "Unassigned",
      Team:                "General Support",
      Category:            "Task Board",
      Priority:            priority,
      Severity:            "Sev-3",
      Urgency:             "Medium",
      IssueType:           "Task",
      Status:              "Open",
      Condition:           "New",
      Tags:                labelsText || "board-task",
      Origin:              "Board",

      // ── Dates ─────────────────────────────────────────────────────────────
      CreatedAt:           now,
      Timestamp:           now,
      DateStarted:         now,
      DateUpdated:         now,
      Deadline:            new Date(deadline).toISOString(),

      // ── Time tracking ─────────────────────────────────────────────────────
      TimeGoal:            timeGoalMap[priority] || "48 hours",
      TimeSpent:           "0",
      TimeRemaining:       "0",
      Calendar:            "Standard Hours",
      JQL:                 `priority = ${priority}`,
      Estimate:            "TBD",

      // ── Incidents / components ────────────────────────────────────────────
      Components:          "General",
      Affected:            "N/A",
      Linked:              "None",
      OpenIncidents:       "0",
      MyIncidents:         "0",
      MajorIncidents:      "0",

      // ── Notes / descriptions ──────────────────────────────────────────────
      Notes:               `Created from board card. Card ID: ${card.id || ""}`,
      InternalNotes:       "Auto-created from board card",
      DescriptionNotes:    "Board-submitted ticket",
      ActivityDescription: `Ticket created at ${new Date().toLocaleString()}`,
      WorkDescription:     "Pending initial review",
      OpenDescription:     `Ticket opened on ${new Date().toLocaleDateString()}`,
      PendingDescription:  "",
      CompleteDescription: "",
      ResolvedDescription: "",
      CloseDescription:    "",
      Reason:              "New ticket submission",

      // ── Flags ─────────────────────────────────────────────────────────────
      Subtask:             "None",
      Followup:            "false",
      Done:                "false",
    };

    setCreating(true);
    try {
      const url = `${config.API_BASE_URL}/Tickets`;
      console.log("%c[CreateTicket] POST →", "color:#579dff;font-weight:bold", url);
      console.log("%c[CreateTicket] Payload:", "color:#579dff", payload);

      // API expects multipart/form-data — do NOT set Content-Type manually
      const fd = new FormData();
      Object.entries(payload).forEach(([k, v]) => {
        if (v === null || v === undefined) return;      // skip nulls
        if (typeof v === "boolean") { fd.append(k, v ? "true" : "false"); return; }
        fd.append(k, String(v));
      });
      // Log what we're actually sending
      console.log("%c[CreateTicket] FormData entries:", "color:#579dff");
      for (const [k, v] of fd.entries()) console.log("  ", k, "=", v);
      const res = await fetch(url, { method: "POST", body: fd });

      console.log("%c[CreateTicket] Response status:", "color:#fbbf24", res.status, res.statusText);

      if (!res.ok) {
        const rawText = await res.text().catch(() => "");
        console.error("%c[CreateTicket] Error body:", "color:#f87168", rawText);

        let msg = `HTTP ${res.status}`;
        try {
          const j = JSON.parse(rawText);
          if (j.errors) {
            // ASP.NET validation errors: { errors: { Field: ["msg"] } }
            msg = Object.entries(j.errors)
              .map(([f, m]) => `• ${f}: ${Array.isArray(m) ? m.join(", ") : m}`)
              .join("\n");
          } else {
            msg = j.title || j.message || j.detail || rawText || msg;
          }
        } catch {
          msg = rawText || msg;
        }
        throw new Error(msg);
      }

      // ── Success ──
      setExistingTicket(ticketNumber);
      setExpanded(false);
      onTicketCreated?.(ticketNumber);
      console.log("%c[CreateTicket] ✅ Created:", "color:#4bce97;font-weight:bold", ticketNumber);

      const pc = PRIORITY_COLORS[priority];
      await Swal.fire({
        ...swalDark, icon: "success", title: "Ticket Created! 🎉",
        html: `
          <div style="text-align:left;font-size:13px;color:#94a3b8;line-height:2.2">
            <div>🎫 Ticket #: <b style="color:#60a5fa;font-family:monospace">${ticketNumber}</b></div>
            <div>📌 Subject: <b style="color:#f1f5f9">${card.title}</b></div>
            <div>🔖 Priority: <b style="color:${pc.text}">${priority}</b></div>
            <div>📅 Deadline: <b style="color:#60a5fa">${new Date(deadline).toLocaleString("en-US",{month:"short",day:"numeric",year:"numeric",hour:"2-digit",minute:"2-digit"})}</b></div>
          </div>`,
        timer: 3500, showConfirmButton: false,
      });
    } catch (e) {
      console.error("%c[CreateTicket] ❌ Caught error:", "color:#f87168;font-weight:bold", e);
      await Swal.fire({
        ...swalDark,
        icon: "error",
        title: "Failed to Create Ticket",
        html: `<pre style="text-align:left;font-size:11px;color:#f87168;white-space:pre-wrap;max-height:200px;overflow:auto;background:#0f172a;padding:10px;border-radius:6px;margin:0">${e.message}</pre>`,
      });
    } finally {
      setCreating(false);
    }
  };

  const wrapBorder = existingTicket ? "#4bce9744" : enabled ? "#579dff44" : C.border2;
  const wrapBg     = existingTicket ? "#4bce9708" : enabled ? "#579dff08" : "transparent";

  return (
    <div style={{ marginTop: 14, borderRadius: 10, border: `1px solid ${wrapBorder}`, background: wrapBg, overflow: "hidden", transition: "all .2s" }}>

      {/* ── Header / Toggle row ── */}
      <div onClick={handleToggle}
        style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", cursor: existingTicket ? "default" : "pointer", userSelect: "none" }}>

        {/* Toggle pill */}
        {!existingTicket && (
          <div style={{ width: 34, height: 18, borderRadius: 9, background: enabled ? C.accent : C.border2, position: "relative", transition: "background .2s", flexShrink: 0 }}>
            <div style={{ position: "absolute", top: 2, left: enabled ? 16 : 2, width: 14, height: 14, borderRadius: "50%", background: "#fff", transition: "left .2s", boxShadow: "0 1px 4px rgba(0,0,0,.4)" }} />
          </div>
        )}

        <span style={{ fontSize: 14 }}>{existingTicket ? "🎫" : "🎟️"}</span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: existingTicket ? C.green : enabled ? C.accent : C.textDim, letterSpacing: ".05em", textTransform: "uppercase" }}>
            {existingTicket ? "Ticket Created" : "Create Ticket"}
          </div>
          {existingTicket
            ? <div style={{ fontSize: 11, color: C.accent, fontFamily: "monospace", marginTop: 1 }}>{existingTicket}</div>
            : <div style={{ fontSize: 10, color: C.textDim, marginTop: 1 }}>Convert this card into a support ticket</div>
          }
        </div>

        {/* Copy btn */}
        {existingTicket && (
          <button
            onClick={e => {
              e.stopPropagation();
              navigator.clipboard?.writeText(existingTicket);
              Swal.fire({ ...swalDark, icon: "success", title: "Copied!", timer: 900, showConfirmButton: false });
            }}
            style={{ background: `${C.accent}18`, border: `1px solid ${C.accent}30`, borderRadius: 5, color: C.accent, fontSize: 10, padding: "2px 8px", cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>
            📋 Copy
          </button>
        )}

        {/* Chevron */}
        {!existingTicket && enabled && (
          <span
            onClick={e => { e.stopPropagation(); setExpanded(p => !p); }}
            style={{ fontSize: 10, color: C.textDim, cursor: "pointer", padding: "2px 4px" }}>
            {expanded ? "▲" : "▼"}
          </span>
        )}
      </div>

      {/* ── Expanded form ── */}
      {enabled && !existingTicket && expanded && (
        <div style={{ padding: "0 12px 14px", borderTop: `1px solid ${C.border2}`, display: "flex", flexDirection: "column", gap: 11 }}>

          {/* Fields preview */}
          <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "9px 11px", marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.textDim, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 3 }}>
              📦 Will be included in ticket
            </div>
            <TRow icon="📌" label="Subject"     value={card.title} />
            {(card.desc || card.description) && (
              <TRow icon="📝" label="Description" value={((card.desc || card.description)+"").slice(0, 60) + ((card.desc||card.description).length > 60 ? "…" : "")} />
            )}
            {Array.isArray(card.checklist) && card.checklist.length > 0 && (
              <TRow icon="✅" label="Checklist" value={`${card.checklist.length} item${card.checklist.length !== 1 ? "s" : ""}`} />
            )}
            {Array.isArray(card.labels) && card.labels.length > 0 && (
              <TRow icon="🏷️" label="Labels" value={card.labels.join(", ")} />
            )}
            <TRow icon="📊" label="Status" value="Open (new ticket)" />
          </div>

          {/* Priority */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, marginBottom: 6 }}>🔖 Priority</div>
            <div style={{ display: "flex", gap: 5 }}>
              {["Critical", "High", "Medium", "Low"].map(p => {
                const pc = PRIORITY_COLORS[p];
                const sel = priority === p;
                return (
                  <button key={p} onClick={() => handlePriorityChange(p)}
                    style={{ flex: 1, padding: "5px 2px", borderRadius: 6, border: `1px solid ${sel ? pc.border : C.border2}`, background: sel ? pc.dim : "transparent", color: sel ? pc.text : C.textDim, fontSize: 11, fontWeight: sel ? 700 : 400, cursor: "pointer", fontFamily: "inherit", transition: "all .12s" }}>
                    {p}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Deadline */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, marginBottom: 6, display: "flex", alignItems: "center", gap: 5 }}>
              📅 Deadline
              <span style={{ fontSize: 10, color: C.red, fontWeight: 400 }}>* required</span>
            </div>
            <input
              type="datetime-local"
              value={deadline}
              min={minDeadline}
              onChange={e => setDeadline(e.target.value)}
              style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px", background: C.bg, border: `1px solid ${deadline ? C.accent + "66" : C.red + "55"}`, borderRadius: 7, color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none" }}
            />
            {deadline && (
              <div style={{ fontSize: 10, color: C.accent, marginTop: 3 }}>
                ✓ {new Date(deadline).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
              </div>
            )}
          </div>

          {/* Create button */}
          <button
            onClick={handleCreate}
            disabled={creating || !deadline}
            style={{ width: "100%", padding: "9px", borderRadius: 8, border: "none", background: creating || !deadline ? C.surfaceHov : C.accent, color: creating || !deadline ? C.textDim : "#fff", fontSize: 13, fontWeight: 700, cursor: creating || !deadline ? "not-allowed" : "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, transition: "all .15s", boxShadow: !creating && deadline ? "0 4px 14px rgba(87,157,255,.3)" : "none" }}
            onMouseEnter={e => { if (!creating && deadline) e.currentTarget.style.background = C.accentHov; }}
            onMouseLeave={e => { if (!creating && deadline) e.currentTarget.style.background = C.accent; }}>
            {creating
              ? <><span style={{ width: 13, height: 13, borderRadius: "50%", border: `2px solid ${C.textDim}`, borderTop: `2px solid ${C.accent}`, animation: "ctspin .7s linear infinite", display: "inline-block" }} /> Creating ticket…</>
              : <>🎫 Create Ticket</>
            }
          </button>
        </div>
      )}
    </div>
  );
}

function TRow({ icon, label, value }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 11 }}>
      <span style={{ flexShrink: 0 }}>{icon}</span>
      <span style={{ color: C.textDim, flexShrink: 0, minWidth: 66 }}>{label}:</span>
      <span style={{ color: C.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{value || "—"}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── AttachmentPanel ───────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
const fmtSize = (b) => b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`;
const isImage = (ct) => ct?.startsWith("image/");

const swalConfirm = async (title, text, confirmText = "Delete") =>
  (await Swal.fire({ ...swalDark, title, text, icon: "warning", showCancelButton: true, confirmButtonText: confirmText, confirmButtonColor: "#ef4444" })).isConfirmed;
const swalError = (text) => Swal.fire({ ...swalDark, icon: "error", title: "Error", text });

function AttachmentPanel({ cardId, onCountChange }) {
  const [attachments, setAttachments] = useState([]);
  const [uploading,   setUploading]   = useState(false);
  const [previewing,  setPreviewing]  = useState(null);
  const [dragActive,  setDragActive]  = useState(false);
  const fileRef = useRef();
  const dlBase  = `${config.API_BASE_URL}/cards/attachments`;

  useEffect(() => {
    if (!cardId) return;
    fetch(`${config.API_BASE_URL}/cards/${cardId}/attachments`)
      .then(r => r.ok ? r.json() : [])
      .then(d => { setAttachments(d); onCountChange?.(d.length); })
      .catch(() => {});
  }, [cardId]);

  const uploadFile = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("uploadedBy", (() => { try { const u = JSON.parse(localStorage.getItem("admin") || localStorage.getItem("user") || "{}"); return u.username || "system"; } catch { return "system"; } })());
    setUploading(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/cards/${cardId}/attachments`, { method: "POST", body: fd });
      if (!res.ok) throw new Error(await res.text());
      const saved = await res.json();
      const upd = [...attachments, saved];
      setAttachments(upd);
      onCountChange?.(upd.length);
    } catch (e) {
      await swalError(`Upload failed: ${e.message}`);
    }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    if (!await swalConfirm("Remove attachment?", "This cannot be undone.", "Remove")) return;
    try {
      await fetch(`${dlBase}/${id}`, { method: "DELETE" });
      const upd = attachments.filter(a => a.id !== id);
      setAttachments(upd);
      onCountChange?.(upd.length);
    } catch {
      await swalError("Failed to delete.");
    }
  };

  const fileIcon = (ct) => {
    if (ct?.includes("pdf"))    return "📄";
    if (ct?.includes("word"))   return "📝";
    if (ct?.includes("sheet") || ct?.includes("excel")) return "📊";
    if (ct?.includes("zip"))    return "🗜️";
    if (ct?.includes("video"))  return "🎬";
    return "📁";
  };

  return (
    <>
      {/* Image lightbox */}
      {previewing && (
        <div
          onClick={() => setPreviewing(null)}
          style={{ position: "fixed", inset: 0, zIndex: 99999, background: "rgba(0,0,0,.92)", backdropFilter: "blur(8px)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, cursor: "zoom-out" }}>
          <img src={previewing.url} alt={previewing.name} onClick={e => e.stopPropagation()} style={{ maxWidth: "88vw", maxHeight: "78vh", borderRadius: 12, objectFit: "contain" }} />
          <div style={{ display: "flex", gap: 12 }}>
            <a href={previewing.url} download={previewing.name} onClick={e => e.stopPropagation()} style={{ background: "#579dff", color: "#fff", borderRadius: 8, padding: "6px 16px", fontSize: 12, fontWeight: 600, textDecoration: "none" }}>↓ Download</a>
            <button onClick={() => setPreviewing(null)} style={{ background: "rgba(255,255,255,.1)", border: "none", borderRadius: 8, color: "#fff", padding: "6px 14px", cursor: "pointer", fontFamily: "inherit" }}>✕ Close</button>
          </div>
        </div>
      )}

      <div>
        {/* Header row */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: ".06em" }}>
            📎 Attachments {attachments.length > 0 && `(${attachments.length})`}
          </span>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            style={{ background: `${C.accent}20`, border: `1px solid ${C.accent}44`, borderRadius: 7, color: C.accent, fontSize: 11, fontWeight: 600, padding: "4px 12px", cursor: "pointer", fontFamily: "inherit" }}>
            {uploading ? "Uploading…" : "+ Add"}
          </button>
          <input ref={fileRef} type="file" accept="*/*" style={{ display: "none" }} onChange={e => { uploadFile(e.target.files?.[0]); e.target.value = ""; }} />
        </div>

        {/* Empty drop zone */}
        {attachments.length === 0 && (
          <div
            onClick={() => fileRef.current?.click()}
            onDrop={e => { e.preventDefault(); setDragActive(false); uploadFile(e.dataTransfer.files?.[0]); }}
            onDragOver={e => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            style={{ border: `2px dashed ${dragActive ? C.accent : C.border2}`, borderRadius: 10, padding: "22px 16px", display: "flex", flexDirection: "column", alignItems: "center", gap: 7, cursor: "pointer", background: dragActive ? `${C.accent}08` : "transparent", transition: "all .15s" }}>
            <span style={{ fontSize: 26 }}>📎</span>
            <span style={{ fontSize: 12, color: C.textMuted }}>Drag & drop or click to attach</span>
            <span style={{ fontSize: 10, color: C.textDim }}>Any file type</span>
          </div>
        )}

        {/* Image grid */}
        {attachments.some(a => isImage(a.contentType)) && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(90px,1fr))", gap: 7, marginBottom: 8 }}>
            {attachments.filter(a => isImage(a.contentType)).map(att => (
              <div key={att.id} style={{ position: "relative", borderRadius: 8, overflow: "hidden", aspectRatio: "4/3", cursor: "pointer" }}
                onClick={() => setPreviewing({ url: `${dlBase}/${att.id}/download`, name: att.fileName })}>
                <img src={`${dlBase}/${att.id}/download`} alt={att.fileName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <button
                  onClick={e => { e.stopPropagation(); handleDelete(att.id); }}
                  style={{ position: "absolute", top: 4, right: 4, background: "rgba(248,113,104,.85)", border: "none", borderRadius: 4, color: "#fff", fontSize: 9, padding: "2px 5px", cursor: "pointer" }}>✕</button>
              </div>
            ))}
          </div>
        )}

        {/* Non-image file list */}
        {attachments.filter(a => !isImage(a.contentType)).map(att => (
          <div key={att.id} style={{ background: C.surfaceHov, border: `1px solid ${C.border2}`, borderRadius: 8, padding: "8px 11px", marginBottom: 5, display: "flex", alignItems: "center", gap: 9 }}>
            <div style={{ width: 32, height: 32, borderRadius: 7, background: `${C.accent}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>{fileIcon(att.contentType)}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{att.fileName}</div>
              <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>{fmtSize(att.fileSize)} · {att.uploadedBy}</div>
            </div>
            <a href={`${dlBase}/${att.id}/download`} target="_blank" rel="noreferrer"
              style={{ background: `${C.accent}18`, border: `1px solid ${C.accent}30`, borderRadius: 5, color: C.accent, fontSize: 11, padding: "3px 8px", textDecoration: "none" }}>↓</a>
            <button onClick={() => handleDelete(att.id)}
              style={{ background: `${C.red}18`, border: `1px solid ${C.red}30`, borderRadius: 5, color: C.red, fontSize: 11, padding: "3px 7px", cursor: "pointer", fontFamily: "inherit" }}>✕</button>
          </div>
        ))}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── CardModal ─────────────────────────────────────────────────────────────────
// Props:
//   card, columns, onClose, onUpdate, onArchive, onDelete
//   statusBadge       — optional ReactNode in header
//   belowDescription  — optional ReactNode rendered right after description textarea
//                       (BoardView passes <CreateTicketSection> here — but we now
//                        handle it internally so this prop is kept for compat only)
//   attachmentPanel   — optional ReactNode; if omitted we render AttachmentPanel internally
// ─────────────────────────────────────────────────────────────────────────────
export function CardModal({ card, columns, onClose, onUpdate, onArchive, onDelete, statusBadge, belowDescription, attachmentPanel, assignedTo }) {
  const [title,       setTitle]       = useState(card.title);
  const [desc,        setDesc]        = useState(card.desc || "");
  const [checklist,   setChecklist]   = useState(card.checklist || []);
  const [labels,      setLabels]      = useState(card.labels || []);
  const [newItem,     setNewItem]     = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [showLabels,  setShowLabels]  = useState(false);
  const [colId,       setColId]       = useState(card.colId);
  const [attachCount, setAttachCount] = useState(0);

  // local ticketRef — stays in sync regardless of how ticket was created
  const [ticketRef, setTicketRef] = useState(card.ticketRef || null);

  const progress = checklistProgress(checklist);

  // auto-save on any change
  useEffect(() => {
    onUpdate({ ...card, title, desc, checklist, labels, colId });
  }, [title, desc, checklist, labels, colId]);

  const addItem = () => {
    if (!newItem.trim()) return;
    setChecklist(p => [...p, { id: uid(), text: newItem.trim(), done: false }]);
    setNewItem("");
  };

  const toggleItem  = id => setChecklist(p => p.map(c => c.id === id ? { ...c, done: !c.done } : c));
  const deleteItem  = id => setChecklist(p => p.filter(c => c.id !== id));
  const updateItem  = (id, text) => setChecklist(p => p.map(c => c.id === id ? { ...c, text } : c));
  const toggleLabel = color => setLabels(p => p.includes(color) ? p.filter(l => l !== color) : [...p, color]);

  // Called when a ticket is created inside this modal
// Called when a ticket is created inside this modal
const handleTicketCreated = async (ticketNumber) => {
  setTicketRef(ticketNumber);
  const updatedCard = { ...card, title, desc, checklist, labels, colId, ticketRef: ticketNumber };

  try {
    const payload = {
      id: card.id ?? 0,
      colId: parseInt(updatedCard.colId, 10),          // ← FIX: ensure int hindi string
      title: updatedCard.title?.trim() || "",
      description: updatedCard.desc ?? "",
      labels: Array.isArray(updatedCard.labels) ? updatedCard.labels.join(",") : (updatedCard.labels ?? ""),
      checklist: Array.isArray(updatedCard.checklist) ? JSON.stringify(updatedCard.checklist) : (updatedCard.checklist ?? "[]"),
      archived: updatedCard.archived ?? false,
      status: updatedCard.status ?? "todo",
      createdBy: updatedCard.createdBy || "system",
      createdAt: updatedCard.createdAt
        ? (typeof updatedCard.createdAt === "number"
            ? new Date(updatedCard.createdAt).toISOString()
            : updatedCard.createdAt)
        : new Date().toISOString(),
      ticketAllowed: updatedCard.ticketAllowed ?? false,
      ticketRef: ticketNumber,
    };

    console.log("%c[handleTicketCreated] PUT payload:", "color:#fbbf24", payload);

    const res = await fetch(`${config.API_BASE_URL}/cards/${card.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errText = await res.text().catch(() => "");
      console.error("%c[handleTicketCreated] PUT failed:", "color:#f87168", res.status, errText);
      throw new Error(`HTTP ${res.status}: ${errText}`);
    }

    const saved = await res.json();
    console.log("%c✅ Card ticketRef saved to DB:", "color:#4bce97;font-weight:bold", saved.ticketRef);

  } catch (e) {
    console.error("Failed to persist ticketRef to card:", e);
  }

  onUpdate(updatedCard);
};
  // Live card object always reflects current state
  const liveCard = { ...card, title, desc, checklist, labels, colId, ticketRef };

  return (
    <>
      <style>{`
        @keyframes ctspin { to { transform: rotate(360deg); } }
        .cm-scroll::-webkit-scrollbar { width: 5px; }
        .cm-scroll::-webkit-scrollbar-thumb { background: ${C.border2}; border-radius: 4px; }
      `}</style>

      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.72)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 9999, padding: "32px 16px 40px", overflowY: "auto" }}>

        {/* Modal box */}
        <div
          onClick={e => e.stopPropagation()}
          style={{ background: "#22272b", borderRadius: 14, width: "100%", maxWidth: 720, boxShadow: "0 24px 64px rgba(0,0,0,.65)", overflow: "hidden" }}>

          {/* ── Top header ── */}
          <div style={{ padding: "20px 20px 0" }}>
            {labels.length > 0 && (
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 10 }}>
                {labels.map(l => <LabelPill key={l} color={l} />)}
              </div>
            )}
            <textarea
              value={title}
              onChange={e => setTitle(e.target.value)}
              style={{ width: "100%", background: "transparent", border: "none", outline: "none", fontSize: 18, fontWeight: 700, color: C.text, resize: "none", fontFamily: "inherit", lineHeight: 1.4 }}
              rows={title.length > 60 ? 2 : 1}
            />

            {/* Column picker + optional status badge */}
            <div style={{ fontSize: 12, color: C.textDim, marginBottom: 14, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span>Column:</span>
              <select
                value={colId}
                onChange={e => setColId(e.target.value)}
                style={{ background: C.surfaceHov, border: `1px solid ${C.border2}`, borderRadius: 4, color: C.textMid, fontSize: 12, padding: "2px 6px", fontFamily: "inherit" }}>
                {columns.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
              {statusBadge && (
                <>
                  <span style={{ color: C.textDim }}>·</span>
                  <span style={{ fontSize: 11, color: C.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>Status:</span>
                  {statusBadge}
                </>
              )}
            </div>
          </div>

          {/* ── Body ── */}
          <div style={{ display: "flex" }}>

            {/* ── Left column ── */}
            <div style={{ flex: 1, padding: "0 20px 24px", minWidth: 0 }}>

              {/* Description */}
              <Section label="📝 Description">
                <textarea
                  value={desc}
                  onChange={e => setDesc(e.target.value)}
                  placeholder="Add a description..."
                  style={{ width: "100%", background: C.surfaceHov, border: `1px solid ${C.border2}`, borderRadius: 6, padding: "10px 12px", fontSize: 13, color: C.textMid, resize: "vertical", outline: "none", fontFamily: "inherit", minHeight: 80, lineHeight: 1.6 }}
                  onFocus={e => e.target.style.borderColor = C.accent}
                  onBlur={e => e.target.style.borderColor = C.border2}
                />

                {/*
                  ── CREATE TICKET ──
                  Rendered directly below the description textarea.
                  If the parent passes `belowDescription` we use that,
                  otherwise we render our own CreateTicketSection.
                */}
                {belowDescription
                  ? belowDescription
                  : (
                    <CreateTicketSection
                      card={liveCard}
                      onTicketCreated={handleTicketCreated}
                      assignedTo={assignedTo}
                    />
                  )
                }
              </Section>

              {/* Checklist */}
              <Section label="☑ Checklist" right={progress && `${progress.done}/${progress.total}`}>
                {progress && <ChecklistBar pct={progress.pct} />}
                <div style={{ display: "flex", flexDirection: "column", gap: 3, marginBottom: 10 }}>
                  {checklist.map(item => (
                    <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", borderRadius: 5, background: C.bg }}>
                      <input
                        type="checkbox"
                        checked={item.done}
                        onChange={() => toggleItem(item.id)}
                        style={{ accentColor: C.accent, width: 15, height: 15, flexShrink: 0, cursor: "pointer" }}
                      />
                      {editingItem === item.id ? (
                        <input
                          autoFocus
                          value={item.text}
                          onChange={e => updateItem(item.id, e.target.value)}
                          onBlur={() => setEditingItem(null)}
                          onKeyDown={e => (e.key === "Enter" || e.key === "Escape") && setEditingItem(null)}
                          style={{ flex: 1, background: C.surfaceHov, border: `1px solid ${C.accent}`, borderRadius: 4, padding: "3px 8px", fontSize: 13, color: C.text, outline: "none", fontFamily: "inherit" }}
                        />
                      ) : (
                        <span
                          onClick={() => setEditingItem(item.id)}
                          style={{ flex: 1, fontSize: 13, color: item.done ? C.textDim : C.textMid, textDecoration: item.done ? "line-through" : "none", cursor: "text" }}>
                          {item.text}
                        </span>
                      )}
                      <span onClick={() => deleteItem(item.id)} style={{ color: C.textDim, cursor: "pointer", fontSize: 16, padding: "0 4px" }}>×</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <input
                    value={newItem}
                    onChange={e => setNewItem(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && addItem()}
                    placeholder="Add an item…"
                    style={{ flex: 1, background: C.surfaceHov, border: `1px solid ${C.border2}`, borderRadius: 6, padding: "7px 10px", fontSize: 13, color: C.textMid, outline: "none", fontFamily: "inherit" }}
                    onFocus={e => e.target.style.borderColor = C.accent}
                    onBlur={e => e.target.style.borderColor = C.border2}
                  />
                  <Btn label="Add" onClick={addItem} primary small />
                </div>
              </Section>

              {/* ── Attachments — inside the modal, below checklist ── */}
              <Section label="📎 Attachments">
                {attachmentPanel
                  ? attachmentPanel
                  : (
                    <AttachmentPanel
                      cardId={card.id}
                      onCountChange={setAttachCount}
                    />
                  )
                }
              </Section>

            </div>

            {/* ── Right sidebar ── */}
            <div style={{ width: 160, padding: "0 16px 24px 0", flexShrink: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textDim, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>Actions</div>

              {/* Attachment count badge */}
              {attachCount > 0 && (
                <div style={{ fontSize: 11, color: C.textDim, background: C.surfaceHov, border: `1px solid ${C.border2}`, borderRadius: 6, padding: "4px 8px", marginBottom: 8, textAlign: "center" }}>
                  📎 {attachCount} file{attachCount !== 1 ? "s" : ""}
                </div>
              )}

              {/* Ticket badge in sidebar */}
              {ticketRef && (
                <div style={{ fontSize: 10, color: C.accent, background: `${C.accent}12`, border: `1px solid ${C.accent}30`, borderRadius: 6, padding: "4px 8px", marginBottom: 8, fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  🎫 {ticketRef}
                </div>
              )}

              {/* Label picker */}
              <div style={{ position: "relative", marginBottom: 4 }}>
                <SidebarBtn icon="🏷" label="Labels" onClick={() => setShowLabels(p => !p)} />
                {showLabels && (
                  <div style={{ position: "absolute", right: 0, top: "100%", zIndex: 10, background: C.surfaceHov, border: `1px solid ${C.borderHov}`, borderRadius: 8, padding: 10, width: 185, boxShadow: "0 8px 24px rgba(0,0,0,.4)" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, marginBottom: 8 }}>LABELS</div>
                    {Object.entries(LABEL_COLORS).map(([key]) => (
                      <div key={key} onClick={() => toggleLabel(key)}
                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 4px", borderRadius: 4, cursor: "pointer", marginBottom: 2, background: labels.includes(key) ? C.border2 : "transparent" }}>
                        <div style={{ width: 28, height: 14, background: LABEL_COLORS[key].bg, borderRadius: 3 }} />
                        <span style={{ fontSize: 12, color: C.textMid }}>{LABEL_NAMES[key]}</span>
                        {labels.includes(key) && <span style={{ marginLeft: "auto", color: C.accent, fontSize: 12 }}>✓</span>}
                      </div>
                    ))}
                    <div onClick={() => setShowLabels(false)} style={{ textAlign: "center", marginTop: 8, fontSize: 12, color: C.textMuted, cursor: "pointer" }}>Close</div>
                  </div>
                )}
              </div>

              <SidebarBtn
                icon={card.archived ? "♻️" : "🗃"}
                label={card.archived ? "Unarchive" : "Archive"}
                onClick={() => { onArchive(card.id); onClose(); }}
                colorVal={card.archived ? C.green : undefined}
              />
              <SidebarBtn
                icon="🗑"
                label="Delete"
                onClick={() => { if (window.confirm("Delete this card?")) { onDelete(card.id); onClose(); } }}
                colorVal={C.red}
              />

              <div style={{ marginTop: 16, fontSize: 11, color: C.textDim, lineHeight: 1.8 }}>
                Created<br />
                <span style={{ color: C.textMuted }}>{new Date(card.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── helper ────────────────────────────────────────────────────────────────────
function Section({ label, right, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
        <span>{label}</span>
        {right && <span style={{ color: C.textMid }}>{right}</span>}
      </div>
      {children}
    </div>
  );
}